/**
 * ศูนย์สมาชิก › เสี่ยงโชคและการ์ดมงคล — ประวัติจุดธูป/ขูดการ์ดจากเทวาลัยออนไลน์ + ทางเข้าเทวาลัย
 * โหลดเมื่อเปิดหน้าต่างเท่านั้น (ไม่ยิง API ทุกครั้งที่เปิดหน้าสมาชิก)
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { currentMemberIdToken } from '../lib/api/memberApi'
import {
  KIND_LABEL,
  fetchTempleConfig,
  fetchTemplePlays,
  filterPlays,
  formatPlayTime,
  playStatusText,
  templeUrl,
  type TempleFilter,
  type TemplePlay,
  type TempleSiteConfig,
} from './templePlaysLogic'

function Digits({ play }: { play: TemplePlay }) {
  const three = play.result?.digits3 || []
  const two = play.result?.digits2 || []
  return (
    <div className="mo-temple-digits" aria-label={`เลขมงคล ${three.join('')} ${two.join('')}`}>
      {three.map((d, i) => <b key={`a${i}`}>{d}</b>)}
      {three.length > 0 && two.length > 0 && <span aria-hidden="true" />}
      {two.map((d, i) => <b key={`b${i}`} className="is-ruby">{d}</b>)}
    </div>
  )
}

export function TemplePlaysDialog({ botId, onClose }: { botId: string; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  // เก็บ onClose ใน ref — ผู้เรียกส่งฟังก์ชันใหม่ทุก render ไม่ควรทำให้โหลดรายการซ้ำ
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const [plays, setPlays] = useState<TemplePlay[] | null>(null)
  const [config, setConfig] = useState<TempleSiteConfig | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<TempleFilter>('all')

  useEffect(() => {
    let alive = true
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onCloseRef.current() }
    document.addEventListener('keydown', onKeyDown)
    void (async () => {
      try {
        const [list, cfg] = await Promise.all([currentMemberIdToken().then((token) => fetchTemplePlays(token)), fetchTempleConfig()])
        if (!alive) return
        setPlays(list)
        setConfig(cfg)
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'โหลดรายการไม่สำเร็จ')
      }
    })()
    return () => { alive = false; document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKeyDown) }
  }, [])

  const shown = plays ? filterPlays(plays, filter) : []
  const count = (kind: TempleFilter) => (plays ? filterPlays(plays, kind).length : 0)

  return createPortal(<div className="mo-benefits-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <section className="mo-benefits-dialog mo-temple" role="dialog" aria-modal="true" aria-label="เสี่ยงโชคและการ์ดมงคล">
      <div className="mo-benefits-head"><span>เสี่ยงโชคและการ์ดมงคล</span><button ref={closeRef} type="button" onClick={onClose} aria-label="ปิด"><i className="fas fa-xmark" aria-hidden="true" /></button></div>

      <a className="mo-temple-entry" href={templeUrl(config, botId)}>
        <img src="/temple-loop-poster.jpg" alt="" decoding="async" />
        <span className="mo-temple-entry-text"><b>เข้าเทวาลัย</b><small>จุดธูปขอเลขมงคล · ขูดการ์ดมงคล</small></span>
        <span className="mo-temple-entry-go">ไปเลย</span>
      </a>

      <div className="mo-tabs mo-temple-tabs" role="tablist" aria-label="ประเภท">
        {(['all', 'incense', 'scratch'] as TempleFilter[]).map((key) => (
          <button key={key} type="button" role="tab" aria-selected={filter === key} className={filter === key ? 'is-on' : ''} onClick={() => setFilter(key)}>
            {key === 'all' ? 'ทั้งหมด' : KIND_LABEL[key]}<span className="mo-count">{count(key)}</span>
          </button>
        ))}
      </div>

      {error && <p className="mo-benefits-empty" role="alert">{error}</p>}
      {!error && !plays && <p className="mo-benefits-empty" aria-busy="true">กำลังโหลด…</p>}
      {plays && shown.length === 0 && <p className="mo-benefits-empty">ยังไม่มีรายการ · แตะ “เข้าเทวาลัย” เพื่อจุดธูปหรือขูดการ์ด</p>}
      <div className="mo-temple-list">
        {shown.map((play) => {
          const status = playStatusText(play)
          const body = (
            <>
              <div className="mo-temple-row-head"><b>{KIND_LABEL[play.kind]}</b><span>{formatPlayTime(play.paidAt || play.createdAt)}</span></div>
              {play.kind === 'incense' && (play.paid || play.free) && <Digits play={play} />}
              {play.kind === 'scratch' && play.card && <p className="mo-temple-card">{play.card.text}</p>}
              {status && <p className="mo-temple-status">{status}</p>}
            </>
          )
          return status
            ? <a key={play.playId} className="mo-temple-row is-pending" href={templeUrl(config, botId, play)}>{body}</a>
            : <div key={play.playId} className="mo-temple-row">{body}</div>
        })}
      </div>
    </section>
  </div>, document.body)
}

/** แถบสั้นบนหน้าออร์เดอร์ของฉัน — เลขมงคลล่าสุดภายใน 14 วัน (ข้อมูลจาก memberApi templeLatest) */
export function TempleLatestStrip({ latest, onOpen }: { latest: { digits3: string[]; digits2: string[]; paidAt: string }; onOpen: () => void }) {
  return (
    <button type="button" className="mo-glass mo-temple-strip" onClick={onOpen}>
      <span className="mo-temple-strip-head"><b>🪔 เลขมงคลล่าสุด</b><small>{formatPlayTime(latest.paidAt)}</small></span>
      <span className="mo-temple-digits">
        {latest.digits3.map((d, i) => <b key={`a${i}`}>{d}</b>)}
        <span aria-hidden="true" />
        {latest.digits2.map((d, i) => <b key={`b${i}`} className="is-ruby">{d}</b>)}
      </span>
      <small className="mo-temple-strip-more">ดูทั้งหมด ›</small>
    </button>
  )
}
